// ============================================================================
// TOURNAMENT SERVICE - Real Firebase + Wallet Integration
// Handles registration with ACTUAL PKCash deduction/refund
// Tournament rake collection with agent commission distribution
// ============================================================================

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import walletService from './walletService';
import gameApiService from './gameApiService';  //  Added for atomic server operations

import {
  Tournament,
  TournamentStatus,
  TournamentTier,
  TournamentRegistration,
  TournamentRakeTransaction,
  TOURNAMENT_TIERS,
  TOURNAMENT_SCHEDULE,
  TOURNAMENT_RAKE_CONFIG,
  DEFAULT_BLIND_LEVELS,
  DEFAULT_PAYOUT_TOP_3,
  calculatePrizePool,
  calculatePayouts,
  calculateTablesNeeded,
  getPayoutStructure,
  getUpcomingTournamentDates,
  generateTournamentName,
  generateTournamentDescription,
  getWeekNumber,
} from '../types/tournamentTypes';

const TOURNAMENTS_COLLECTION = 'tournaments';
const TOURNAMENT_RAKE_COLLECTION = 'tournamentRake';

// ============================================================================
// TOURNAMENT SERVICE CLASS
// ============================================================================

class TournamentService {

  // ==========================================================================
  // CRUD OPERATIONS (Admin Only)
  // ==========================================================================

  /**
   * Create a new tournament
   * Registration is ALWAYS open from creation until start time
   */
  async createTournament(
    tournament: Omit<Tournament, 'id' | 'createdAt' | 'updatedAt' | 'currentPlayers' | 'registeredPlayers' | 'prizePool'>
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      
      // Calculate entry fee (house rake) from buy-in
      const houseRakePercent = tournament.houseRakePercent || 10;
      const entryFee = tournament.entryFee || Math.floor(tournament.buyInAmount * (houseRakePercent / 100) * 100) / 100;
      
      // Registration is ALWAYS open from now until tournament starts
      const registrationOpenTime = new Date(); // Open immediately
      const registrationCloseTime = tournament.startTime; // Closes at start
      
      const docRef = await addDoc(collection(db, TOURNAMENTS_COLLECTION), {
        ...tournament,
        startTime: Timestamp.fromDate(tournament.startTime),
        registrationOpenTime: Timestamp.fromDate(registrationOpenTime),
        registrationCloseTime: Timestamp.fromDate(registrationCloseTime),
        lateRegistrationEndTime: tournament.lateRegistrationEndTime 
          ? Timestamp.fromDate(tournament.lateRegistrationEndTime) 
          : null,
        status: 'registering', // Always start as registering (open)
        registeredPlayers: [],
        currentPlayers: 0,
        prizePool: 0,
        entryFee,
        houseRakePercent,
        tablesCount: 0,
        currentBlindLevel: 1,
        totalRakeCollected: 0,
        agentRakePaid: 0,
        houseRakeNet: 0,
        createdAt: now,
        updatedAt: now,
      });

      console.log('✅ [Tournament] Created:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ [Tournament] Create failed:', error);
      throw error;
    }
  }

  /**
   * Create a scheduled tournament from tier config
   * Registration is ALWAYS open from creation
   */
  async createTournamentFromTier(
    tier: TournamentTier,
    startTime: Date,
    createdBy: string,
    options?: { name?: string; description?: string; guaranteedPrize?: number; weekNumber?: number }
  ): Promise<string> {
    const config = TOURNAMENT_TIERS[tier];
    
    // Registration opens NOW (always open)
    const registrationOpenTime = new Date();
    const registrationCloseTime = new Date(startTime.getTime()); // At start
    
    // Late registration ends after level 4
    const lateRegMinutes = config.lateRegLevels * config.blindLevelDuration;
    const lateRegistrationEndTime = new Date(startTime.getTime() + (lateRegMinutes * 60 * 1000));

    return this.createTournament({
      name: options?.name || generateTournamentName(tier, startTime),
      description: options?.description || generateTournamentDescription(tier),
      tier,
      startTime,
      registrationOpenTime,
      registrationCloseTime,
      lateRegistrationEndTime,
      buyInAmount: config.buyIn,
      currency: config.currency,
      entryFee: Math.floor(config.buyIn * (config.houseRakePercent / 100) * 100) / 100,
      houseRakePercent: config.houseRakePercent,
      type: 'freezeout',
      startingChips: config.startingChips,
      blindLevels: DEFAULT_BLIND_LEVELS,
      blindLevelDuration: config.blindLevelDuration,
      lateRegLevels: config.lateRegLevels,
      decisionTimerSeconds: config.decisionTimerSeconds,
      timeBankSeconds: config.timeBankSeconds,
      timeBankUsesPerHand: config.timeBankUsesPerHand,
      minPlayers: config.minPlayersToStart,
      maxPlayers: 9999, // Unlimited
      maxPlayersPerTable: config.maxPlayersPerTable,
      payoutStructure: DEFAULT_PAYOUT_TOP_3,
      guaranteedPrize: options?.guaranteedPrize || 0,
      createdBy,
      isRecurring: true,
      isFeatured: tier === 'high',
      weekNumber: options?.weekNumber || getWeekNumber(startTime),
    });
  }

  /**
   * Generate all tournaments for the upcoming week
   * Called by Cloud Function every Sunday at midnight
   */
  async generateWeeklyTournaments(createdBy: string): Promise<{ created: number; skipped: number }> {
    console.log('🗓️ [Tournament] Generating weekly tournaments...');
    
    const upcomingDates = getUpcomingTournamentDates(7); // Next 7 days
    let created = 0;
    let skipped = 0;

    console.log(`   📅 Found ${upcomingDates.length} tournament slots to check`);

    for (const { date, tier } of upcomingDates) {
      // Check if tournament already exists for this date/tier
      const existingQuery = query(
        collection(db, TOURNAMENTS_COLLECTION),
        where('tier', '==', tier),
        where('status', 'in', ['registering', 'upcoming', 'scheduled', 'starting'])
      );
      
      const existing = await getDocs(existingQuery);
      const existsForDate = existing.docs.some(doc => {
        const data = doc.data();
        const existingDate = data.startTime?.toDate();
        if (!existingDate) return false;
        return Math.abs(existingDate.getTime() - date.getTime()) < 60000; // Within 1 minute
      });

      if (existsForDate) {
        console.log(`   ⏭️ Skipping ${tier} at ${date.toISOString()} - already exists`);
        skipped++;
        continue;
      }

      try {
        await this.createTournamentFromTier(tier, date, createdBy, {
          weekNumber: getWeekNumber(date),
        });
        created++;
        console.log(`   ✅ Created ${tier} tournament for ${date.toISOString()}`);
      } catch (error) {
        console.error(`   ❌ Failed to create ${tier} tournament:`, error);
      }
    }

    console.log(`🗓️ [Tournament] Generation complete: ${created} created, ${skipped} skipped`);
    return { created, skipped };
  }

  /**
   * Update a tournament
   */
  async updateTournament(tournamentId: string, updates: Partial<Tournament>): Promise<void> {
    try {
      const docRef = doc(db, TOURNAMENTS_COLLECTION, tournamentId);
      
      const processedUpdates: any = { ...updates, updatedAt: Timestamp.now() };
      
      if (updates.startTime) {
        processedUpdates.startTime = Timestamp.fromDate(updates.startTime);
      }
      if (updates.registrationOpenTime) {
        processedUpdates.registrationOpenTime = Timestamp.fromDate(updates.registrationOpenTime);
      }
      if (updates.registrationCloseTime) {
        processedUpdates.registrationCloseTime = Timestamp.fromDate(updates.registrationCloseTime);
      }
      if (updates.lateRegistrationEndTime) {
        processedUpdates.lateRegistrationEndTime = Timestamp.fromDate(updates.lateRegistrationEndTime);
      }
      
      await updateDoc(docRef, processedUpdates);
      console.log('✅ [Tournament] Updated:', tournamentId);
    } catch (error) {
      console.error('❌ [Tournament] Update failed:', error);
      throw error;
    }
  }

  /**
   * Delete a tournament
   */
  async deleteTournament(tournamentId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, TOURNAMENTS_COLLECTION, tournamentId));
      console.log('✅ [Tournament] Deleted:', tournamentId);
    } catch (error) {
      console.error('❌ [Tournament] Delete failed:', error);
      throw error;
    }
  }

  /**
   * Update tournament status
   */
  async updateStatus(tournamentId: string, status: TournamentStatus): Promise<void> {
    await this.updateTournament(tournamentId, { status });
  }

  // ==========================================================================
  // READ OPERATIONS
  // ==========================================================================

  /**
   * Get a single tournament by ID
   */
  async getTournament(tournamentId: string): Promise<Tournament | null> {
    try {
      const docSnap = await getDoc(doc(db, TOURNAMENTS_COLLECTION, tournamentId));
      
      if (!docSnap.exists()) {
        return null;
      }

      return this.convertToTournament(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('❌ [Tournament] Get failed:', error);
      throw error;
    }
  }

  /**
   * Get all upcoming tournaments (registration open)
   */
  async getUpcomingTournaments(): Promise<Tournament[]> {
    try {
      const q = query(
        collection(db, TOURNAMENTS_COLLECTION),
        where('status', 'in', ['upcoming', 'registering', 'scheduled', 'starting', 'late_reg']),
        orderBy('startTime', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.convertToTournament(doc.id, doc.data()));
    } catch (error) {
      console.error('❌ [Tournament] Get upcoming failed:', error);
      return [];
    }
  }

  /**
   * Get running tournaments
   */
  async getRunningTournaments(): Promise<Tournament[]> {
    try {
      const q = query(
        collection(db, TOURNAMENTS_COLLECTION),
        where('status', 'in', ['running', 'in_progress', 'late_reg', 'final_table', 'heads_up']),
        orderBy('startTime', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.convertToTournament(doc.id, doc.data()));
    } catch (error) {
      console.error('❌ [Tournament] Get running failed:', error);
      return [];
    }
  }

  /**
   * Get completed tournaments
   */
  async getCompletedTournaments(limitCount: number = 20): Promise<Tournament[]> {
    try {
      const q = query(
        collection(db, TOURNAMENTS_COLLECTION),
        where('status', '==', 'completed'),
        orderBy('startTime', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.convertToTournament(doc.id, doc.data()));
    } catch (error) {
      console.error('❌ [Tournament] Get completed failed:', error);
      return [];
    }
  }

  /**
   * Subscribe to upcoming tournaments (real-time)
   */
  subscribeToUpcomingTournaments(callback: (tournaments: Tournament[]) => void): () => void {
    const q = query(
      collection(db, TOURNAMENTS_COLLECTION),
      //  Include 'running' so users can spectate live tournaments from the

      // same lobby. The card UI swaps the Register button for a Spectate
      // button when status === 'running'.
      where('status', 'in', ['upcoming', 'registering', 'scheduled', 'starting', 'late_reg', 'running']),
      orderBy('startTime', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const tournaments = snapshot.docs.map(doc => 
        this.convertToTournament(doc.id, doc.data())
      );
      callback(tournaments);
    }, (error) => {
      console.error('❌ [Tournament] Subscription error:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to a single tournament (real-time)
   */
  subscribeToTournament(
    tournamentId: string,
    callback: (tournament: Tournament | null) => void
  ): () => void {
    const docRef = doc(db, TOURNAMENTS_COLLECTION, tournamentId);

    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(this.convertToTournament(snapshot.id, snapshot.data()));
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('❌ [Tournament] Tournament subscription error:', error);
      callback(null);
    });
  }

  /**
   * Subscribe to all tournaments (real-time) - for admin
   */
  subscribeToAllTournaments(callback: (tournaments: Tournament[]) => void): () => void {
    const q = query(
      collection(db, TOURNAMENTS_COLLECTION),
      orderBy('startTime', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const tournaments = snapshot.docs.map(doc => 
        this.convertToTournament(doc.id, doc.data())
      );
      callback(tournaments);
    }, (error) => {
      console.error('❌ [Tournament] All subscription error:', error);
      callback([]);
    });
  }

  // ==========================================================================
  // REGISTRATION - WITH REAL WALLET TRANSACTIONS
  // ==========================================================================

  /**
   * Register a user for a tournament
   *  ACTUALLY DEDUCTS PKCash from wallet!

   */
  async registerPlayer(
    tournamentId: string,
    userId: string,
    inviteCode?: string
  ): Promise<{ success: boolean; error?: string }> {
    console.log(`🎟️ [Tournament] Registering ${userId} for ${tournamentId}`);

    try {
      const tournament = await this.getTournament(tournamentId);
      
      if (!tournament) {
        return { success: false, error: 'Tournament not found' };
      }

      // Check tournament status - registration is open for these statuses
      if (!['registering', 'upcoming', 'scheduled', 'late_reg'].includes(tournament.status)) {
        return { success: false, error: 'Registration is closed' };
      }

      // Check if already registered
      if (tournament.registeredPlayers.includes(userId)) {
        return { success: false, error: 'Already registered' };
      }

      // Calculate total cost (buy-in)
      const totalCost = tournament.buyInAmount;

      // Client-side balance check for quick UX feedback (server will re-validate)
      const balance = await walletService.getBalance(userId, tournament.currency);
      if (balance < totalCost) {
        return { 
          success: false, 
          error: `Insufficient ${tournament.currency === 'pkcash' ? 'PKCash' : 'PlayCoins'}. Need $${totalCost}, have $${balance.toFixed(2)}` 
        };
      }

      //  FIX: Use atomic server API instead of direct wallet mutation

      // Server handles: validation, wallet deduction, tournament update in single transaction
      const result = await gameApiService.registerTournament(tournamentId, inviteCode);
      
      if (!result.success) {
        return { success: false, error: result.error || 'Registration failed' };
      }

      console.log(`✅ [Tournament] Registered via server API! Entry fee: $${result.entryFee || totalCost}`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ [Tournament] Registration failed:', error);
      return { success: false, error: error.message || 'Registration failed' };
    }
  }

  /**
   * Unregister a user from a tournament
   *  REFUNDS via atomic server API

   */
  async unregisterPlayer(
    tournamentId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string; refundAmount?: number }> {
    console.log(`🚪 [Tournament] Unregistering ${userId} from ${tournamentId}`);

    try {
      const tournament = await this.getTournament(tournamentId);
      
      if (!tournament) {
        return { success: false, error: 'Tournament not found' };
      }

      // Client-side status check for quick UX feedback (server will re-validate)
      if (['running', 'in_progress', 'final_table', 'heads_up', 'completed'].includes(tournament.status)) {
        return { success: false, error: 'Cannot unregister from active tournament' };
      }

      // Check if registered
      if (!tournament.registeredPlayers.includes(userId)) {
        return { success: false, error: 'Not registered' };
      }

      //  FIX: Use atomic server API instead of direct wallet mutation

      // Server handles: validation, wallet refund, tournament update in single transaction
      const result = await gameApiService.unregisterTournament(tournamentId);
      
      if (!result.success) {
        return { success: false, error: result.error || 'Unregister failed' };
      }

      console.log(`✅ [Tournament] Unregistered via server API! Refund: $${result.refundAmount}`);
      return { success: true, refundAmount: result.refundAmount };

    } catch (error: any) {
      console.error('❌ [Tournament] Unregister failed:', error);
      return { success: false, error: error.message || 'Unregister failed' };
    }
  }

  /**
   * Check if user is registered for a tournament
   */
  async isPlayerRegistered(tournamentId: string, userId: string): Promise<boolean> {
    try {
      const tournament = await this.getTournament(tournamentId);
      return tournament?.registeredPlayers.includes(userId) || false;
    } catch (error) {
      console.error('❌ [Tournament] Error checking registration:', error);
      return false;
    }
  }

  /**
   * Get all tournaments a user is registered for
   */
  async getPlayerTournaments(userId: string): Promise<Tournament[]> {
    try {
      const q = query(
        collection(db, TOURNAMENTS_COLLECTION),
        where('registeredPlayers', 'array-contains', userId),
        where('status', 'in', ['registering', 'upcoming', 'scheduled', 'starting', 'late_reg', 'running', 'in_progress']),
        orderBy('startTime', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.convertToTournament(doc.id, doc.data()));
    } catch (error) {
      console.error('❌ [Tournament] Get player tournaments failed:', error);
      return [];
    }
  }

  // ==========================================================================
  // TOURNAMENT LIFECYCLE
  // ==========================================================================

  /**
   * Start a tournament (called when start time is reached)
   * - Checks minimum players
   * - Collects rake and distributes to agents
   * - Either starts the tournament or cancels with refunds
   */
  async startTournament(tournamentId: string): Promise<{ 
    success: boolean; 
    error?: string;
    rakeCollected?: number;
    agentPayouts?: { agentId: string; amount: number }[];
  }> {
    console.log(`🎬 [Tournament] Starting tournament ${tournamentId}`);

    try {
      const tournament = await this.getTournament(tournamentId);
      
      if (!tournament) {
        return { success: false, error: 'Tournament not found' };
      }

      // Check if already started
      if (['running', 'in_progress', 'completed', 'cancelled'].includes(tournament.status)) {
        return { success: false, error: 'Tournament already started or ended' };
      }

      // Check minimum players
      if (tournament.currentPlayers < tournament.minPlayers) {
        console.log(`❌ [Tournament] Not enough players (${tournament.currentPlayers}/${tournament.minPlayers}), cancelling...`);
        const cancelResult = await this.cancelTournament(tournamentId);
        return { 
          success: false, 
          error: `Not enough players (${tournament.currentPlayers}/${tournament.minPlayers}). Tournament cancelled with full refunds.` 
        };
      }

      //  COLLECT TOURNAMENT RAKE AND DISTRIBUTE TO AGENTS

      const rakeResult = await this.collectTournamentRake(tournament);

      // Update tournament to running/late_reg status
      await updateDoc(doc(db, TOURNAMENTS_COLLECTION, tournamentId), {
        status: 'late_reg', // Late reg is open until level 4
        actualStartTime: Timestamp.now(),
        activePlayers: tournament.registeredPlayers,
        totalRakeCollected: rakeResult.totalRake,
        agentRakePaid: rakeResult.totalAgentPayout,
        houseRakeNet: rakeResult.houseRakeNet,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ [Tournament] Started! Players: ${tournament.currentPlayers}`);
      console.log(`   Rake collected: $${rakeResult.totalRake}`);
      console.log(`   Agent payouts: $${rakeResult.totalAgentPayout}`);
      console.log(`   House net: $${rakeResult.houseRakeNet}`);

      return { 
        success: true, 
        rakeCollected: rakeResult.totalRake,
        agentPayouts: rakeResult.agentPayouts,
      };

    } catch (error: any) {
      console.error('❌ [Tournament] Start error:', error);
      return { success: false, error: error.message || 'Failed to start' };
    }
  }

  /**
   * Cancel tournament and refund all players
   */
  async cancelTournament(tournamentId: string): Promise<{ success: boolean; refundCount: number }> {
    console.log(`❌ [Tournament] Cancelling tournament ${tournamentId}`);

    try {
      const tournament = await this.getTournament(tournamentId);
      
      if (!tournament) {
        return { success: false, refundCount: 0 };
      }

      // Client-side status check for quick UX feedback (server will re-validate)
      if (tournament.status === 'completed' || tournament.status === 'cancelled') {
        return { success: false, refundCount: 0 };
      }

      //  FIX: Use atomic server API instead of looping through players

      // Server handles: bulk refunds, status update in single transaction
      const result = await gameApiService.cancelTournament(tournamentId);
      
      if (!result.success) {
        console.error('❌ [Tournament] Server cancel failed:', result.error);
        return { success: false, refundCount: 0 };
      }

      const refundCount = result.refunds?.length || tournament.registeredPlayers.length;
      console.log(`✅ [Tournament] Cancelled via server API, refunded ${refundCount} players`);
      return { success: true, refundCount };

    } catch (error) {
      console.error('❌ [Tournament] Cancel error:', error);
      return { success: false, refundCount: 0 };
    }
  }

  /**
   * Increment blind level
   */
  async incrementBlindLevel(tournamentId: string): Promise<void> {
    try {
      const tournament = await this.getTournament(tournamentId);
      if (!tournament) return;

      const nextLevel = (tournament.currentBlindLevel || 1) + 1;

      const updates: any = {
        currentBlindLevel: nextLevel,
        updatedAt: Timestamp.now(),
      };

      // Close late registration after level 4 (or configured lateRegLevels)
      if (nextLevel > (tournament.lateRegLevels || 4) && tournament.status === 'late_reg') {
        updates.status = 'running';
        console.log(`🔒 [Tournament] Late registration closed at level ${nextLevel}`);
      }

      await updateDoc(doc(db, TOURNAMENTS_COLLECTION, tournamentId), updates);

    } catch (error) {
      console.error('❌ [Tournament] Error incrementing blind level:', error);
    }
  }

  /**
   * Award prize to player
   */
  async awardPrize(
    tournamentId: string,
    userId: string,
    position: number,
    prizeAmount: number
  ): Promise<void> {
    try {
      if (prizeAmount <= 0) return;

      //  FIX: Use atomic server API instead of direct wallet mutation

      const result = await gameApiService.awardTournamentPrize(
        tournamentId,
        userId,
        position,
        prizeAmount
      );
      
      if (result.success) {
        console.log(`🏆 [Tournament] Awarded $${prizeAmount} to ${userId} for ${position}${this.getOrdinalSuffix(position)} place via server API`);
      } else {
        console.error('❌ [Tournament] Server prize award failed:', result.error);
      }
    } catch (error) {
      console.error('❌ [Tournament] Error awarding prize:', error);
    }
  }

  /**
   * Complete tournament with results
   *  Uses atomic server API for batch prize distribution

   */
  async completeTournament(
    tournamentId: string,
    winnerId: string,
    results: { userId: string; position: number; prizeWon: number }[]
  ): Promise<void> {
    try {
      //  FIX: Use atomic server API for batch prize distribution

      // Server handles: all prize awards + tournament finalization in single transaction
      const apiResults = results
        .filter(r => r.prizeWon > 0)
        .map(r => ({
          userId: r.userId,  //  FIX: Use userId not odD

          position: r.position,
          prizeAmount: r.prizeWon
        }));

      const result = await gameApiService.completeTournament(tournamentId, apiResults);
      
      if (result.success) {
        console.log(`🏆 [Tournament] Completed via server API! Winner: ${winnerId}, ${result.awarded?.length || 0} prizes awarded`);
      } else {
        console.error('❌ [Tournament] Server complete failed, falling back to individual awards');
        // Fallback: award prizes individually
        for (const r of results) {
          if (r.prizeWon > 0) {
            await this.awardPrize(tournamentId, r.userId, r.position, r.prizeWon);
          }
        }
        // Update tournament status directly
        await updateDoc(doc(db, TOURNAMENTS_COLLECTION, tournamentId), {
          status: 'completed',
          winnerId,
          endTime: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('❌ [Tournament] Complete error:', error);
    }
  }

  // ==========================================================================
  // TOURNAMENT RAKE COLLECTION & AGENT DISTRIBUTION
  // ==========================================================================

  /**
   * Collect tournament rake and distribute to agents
   * Called when tournament starts with minimum players met
   *  Uses atomic server API for bulk rake distribution

   */
  async collectTournamentRake(tournament: Tournament): Promise<{
    totalRake: number;
    totalAgentPayout: number;
    houseRakeNet: number;
    agentPayouts: { agentId: string; amount: number; tier: string }[];
  }> {
    console.log(`💰 [Tournament Rake] Collecting rake for ${tournament.name}`);

    const totalBuyIns = tournament.currentPlayers * tournament.buyInAmount;
    const { houseRake: totalRake } = calculatePrizePool(totalBuyIns, tournament.houseRakePercent || 10);

    if (totalRake <= 0) {
      return { totalRake: 0, totalAgentPayout: 0, houseRakeNet: 0, agentPayouts: [] };
    }

    console.log(`   Total buy-ins: $${totalBuyIns}`);
    console.log(`   Total rake: $${totalRake}`);

    try {
      //  FIX: Use atomic server API instead of looping through agents

      // Server handles: rake calculation, all agent payouts, transaction logging atomically
      const result = await gameApiService.collectTournamentRake(tournament.id);
      
      if (result.success) {
        const agentPayouts = (result.agentPayouts || []).map((p: any) => ({
          agentId: p.agentId,
          amount: p.amount,
          tier: p.tier || 'agent',
          playerCount: 0  // Server doesn't return this detail
        }));
        
        const totalAgentPayout = agentPayouts.reduce((sum: number, p: any) => sum + p.amount, 0);
        const houseRakeNet = (result.totalRake || totalRake) - totalAgentPayout;
        
        console.log(`   ✅ Rake collected via server API: $${result.totalRake}`);
        console.log(`   Agent payouts: $${totalAgentPayout} (${agentPayouts.length} agents)`);
        console.log(`   House net: $${houseRakeNet}`);
        
        // Update platform tournament rake totals locally
        await this.updatePlatformTournamentRake(houseRakeNet, tournament.currency);
        
        return { 
          totalRake: result.totalRake || totalRake, 
          totalAgentPayout, 
          houseRakeNet, 
          agentPayouts 
        };
      } else {
        console.error('❌ [Tournament Rake] Server collection failed:', result.error);
        // Fallback to local calculation (no actual payouts)
        return { totalRake, totalAgentPayout: 0, houseRakeNet: totalRake, agentPayouts: [] };
      }
    } catch (error) {
      console.error('❌ [Tournament Rake] Error:', error);
      return { totalRake, totalAgentPayout: 0, houseRakeNet: totalRake, agentPayouts: [] };
    }
  }

  /**
   * Calculate agent rake shares based on registered players
   */
  private async calculateAgentRakeShares(
    playerIds: string[],
    totalRake: number,
    currency: string
  ): Promise<{ agentId: string; amount: number; tier: string; playerCount: number }[]> {
    const agentSharesMap = new Map<string, { amount: number; tier: string; playerCount: number }>();

    // Each player contributes equally to the rake
    const rakePerPlayer = totalRake / playerIds.length;

    for (const playerId of playerIds) {
      try {
        // Get root agent for this player
        const userDoc = await getDoc(doc(db, 'users', playerId));
        if (!userDoc.exists()) continue;

        const rootAgentId = userDoc.data().rootAgentId;
        if (!rootAgentId) continue;

        // Block self-play (agent doesn't earn from their own play)
        if (rootAgentId === playerId) continue;

        // Get agent info
        const agentDoc = await getDoc(doc(db, 'agents', rootAgentId));
        if (!agentDoc.exists() || !agentDoc.data().active) continue;

        const agentData = agentDoc.data();
        const tier = agentData.tier || 'standard';
        const rakePercent = TOURNAMENT_RAKE_CONFIG.agentTiers[tier as keyof typeof TOURNAMENT_RAKE_CONFIG.agentTiers] || 0.10;

        // Calculate agent's share of this player's rake
        const agentShare = rakePerPlayer * rakePercent;

        if (agentSharesMap.has(rootAgentId)) {
          const existing = agentSharesMap.get(rootAgentId)!;
          existing.amount += agentShare;
          existing.playerCount += 1;
        } else {
          agentSharesMap.set(rootAgentId, {
            amount: agentShare,
            tier,
            playerCount: 1,
          });
        }
      } catch (error) {
        console.error(`Error processing player ${playerId}:`, error);
      }
    }

    // Convert to array and round amounts
    return Array.from(agentSharesMap.entries()).map(([agentId, data]) => ({
      agentId,
      amount: Math.round(data.amount * 100) / 100,
      tier: data.tier,
      playerCount: data.playerCount,
    })).filter(p => p.amount > 0);
  }

  /**
   * Update agent tournament rake stats
   */
  private async updateAgentTournamentStats(agentId: string, amount: number): Promise<void> {
    try {
      const agentRef = doc(db, 'agents', agentId);
      await updateDoc(agentRef, {
        totalRakeEarned: increment(amount),
        tournamentRakeEarned: increment(amount),
        lastRakeDate: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Also update referralStats
      const statsRef = doc(db, 'referralStats', agentId);
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          totalRakeEarned: increment(amount),
          tournamentRakeEarned: increment(amount),
          tournamentsRaked: increment(1),
          lastRakeDate: Timestamp.now(),
        });
      } else {
        await addDoc(collection(db, 'referralStats'), {
          referrerId: agentId,
          totalRakeEarned: amount,
          tournamentRakeEarned: amount,
          tournamentsRaked: 1,
          lastRakeDate: Timestamp.now(),
          createdAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating agent tournament stats:', error);
    }
  }

  /**
   * Record tournament rake transaction for admin tracking
   */
  private async recordTournamentRakeTransaction(tx: TournamentRakeTransaction): Promise<void> {
    try {
      await addDoc(collection(db, TOURNAMENT_RAKE_COLLECTION), {
        ...tx,
        timestamp: Timestamp.fromDate(tx.timestamp),
      });

      // Also add to main transactions collection
      await addDoc(collection(db, 'transactions'), {
        userId: 'PLATFORM',
        type: 'tournament_rake',
        currency: tx.currency,
        amount: tx.totalRake,
        description: `Tournament rake: ${tx.tournamentName}`,
        tournamentId: tx.tournamentId,
        houseRake: tx.houseRake,
        agentPayouts: tx.agentPayouts,
        playerCount: tx.playerCount,
        timestamp: Timestamp.fromDate(tx.timestamp),
        status: 'completed',
      });
    } catch (error) {
      console.error('Error recording tournament rake transaction:', error);
    }
  }

  /**
   * Update platform tournament rake totals
   */
  private async updatePlatformTournamentRake(amount: number, currency: string): Promise<void> {
    try {
      const statsRef = doc(db, 'platformStats', 'tournamentRake');
      const statsDoc = await getDoc(statsRef);

      const field = currency === 'pkcash' ? 'totalPKCashRake' : 'totalPlayCoinsRake';

      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          [field]: increment(amount),
          totalTournamentsRaked: increment(1),
          lastRakeDate: Timestamp.now(),
        });
      } else {
        await addDoc(collection(db, 'platformStats'), {
          totalPKCashRake: currency === 'pkcash' ? amount : 0,
          totalPlayCoinsRake: currency === 'playcoins' ? amount : 0,
          totalTournamentsRaked: 1,
          lastRakeDate: Timestamp.now(),
          createdAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating platform tournament rake:', error);
    }
  }

  /**
   * Get tournament rake statistics for admin
   */
  async getTournamentRakeStats(): Promise<{
    totalRake: number;
    totalAgentPayouts: number;
    totalHouseNet: number;
    tournamentsCount: number;
    recentTransactions: TournamentRakeTransaction[];
  }> {
    try {
      // Get platform totals
      const statsDoc = await getDoc(doc(db, 'platformStats', 'tournamentRake'));
      const stats = statsDoc.exists() ? statsDoc.data() : {};

      // Get recent transactions
      const q = query(
        collection(db, TOURNAMENT_RAKE_COLLECTION),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      const transactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      })) as TournamentRakeTransaction[];

      // Calculate totals from transactions if platform stats not available
      const totalRake = stats.totalPKCashRake || transactions.reduce((sum, t) => sum + t.totalRake, 0);
      const totalAgentPayouts = transactions.reduce((sum, t) => 
        sum + t.agentPayouts.reduce((s, p) => s + p.amount, 0), 0);
      const totalHouseNet = totalRake - totalAgentPayouts;

      return {
        totalRake,
        totalAgentPayouts,
        totalHouseNet,
        tournamentsCount: stats.totalTournamentsRaked || transactions.length,
        recentTransactions: transactions,
      };
    } catch (error) {
      console.error('Error getting tournament rake stats:', error);
      return {
        totalRake: 0,
        totalAgentPayouts: 0,
        totalHouseNet: 0,
        tournamentsCount: 0,
        recentTransactions: [],
      };
    }
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  /**
   * Convert Firestore document to Tournament object
   */
  private convertToTournament(id: string, data: any): Tournament {
    return {
      // Everything the server writes rides along (structure, eliminated
      // players, level clock, hand-for-hand, seating map, host info...).
      // The typed fields below override the raw ones with parsed values.
      ...(data || {}),
      id,
      name: data.name,
      description: data.description,
      tier: data.tier,
      startTime: data.startTime?.toDate() || new Date(),
      registrationOpenTime: data.registrationOpenTime?.toDate() || new Date(),
      registrationCloseTime: data.registrationCloseTime?.toDate() || new Date(),
      lateRegistrationEndTime: data.lateRegistrationEndTime?.toDate() || null,
      actualStartTime: data.actualStartTime?.toDate(),
      endTime: data.endTime?.toDate(),
      buyInAmount: data.buyInAmount || 0,
      currency: data.currency || 'pkcash',
      entryFee: data.entryFee || 0,
      houseRakePercent: data.houseRakePercent || 10,
      type: data.type || 'freezeout',
      startingChips: data.startingChips || 5000,
      blindLevels: data.blindLevels || DEFAULT_BLIND_LEVELS,
      blindLevelDuration: data.blindLevelDuration || 10,
      currentBlindLevel: data.currentBlindLevel || 1,
      lateRegLevels: data.lateRegLevels || 4,
      decisionTimerSeconds: data.decisionTimerSeconds || 15,
      timeBankSeconds: data.timeBankSeconds || 10,
      timeBankUsesPerHand: data.timeBankUsesPerHand || 1,
      minPlayers: data.minPlayers || 5,
      maxPlayers: data.maxPlayers || 9999,
      maxPlayersPerTable: data.maxPlayersPerTable || 9,
      prizePool: data.prizePool || 0,
      guaranteedPrize: data.guaranteedPrize,
      payoutStructure: data.payoutStructure || DEFAULT_PAYOUT_TOP_3,
      status: data.status || 'registering',
      registeredPlayers: data.registeredPlayers || [],
      activePlayers: data.activePlayers || [],
      currentPlayers: data.currentPlayers || 0,
      tablesCount: data.tablesCount || 0,
      totalRakeCollected: data.totalRakeCollected || 0,
      agentRakePaid: data.agentRakePaid || 0,
      houseRakeNet: data.houseRakeNet || 0,
      winnerId: data.winnerId,
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || '',
      updatedAt: data.updatedAt?.toDate() || new Date(),
      isRecurring: data.isRecurring || false,
      isFeatured: data.isFeatured || false,
      weekNumber: data.weekNumber,
      password: data.password,
      rebuyAllowed: data.rebuyAllowed,
      rebuyLimit: data.rebuyLimit,
      addonAllowed: data.addonAllowed,
      addonChips: data.addonChips,
      addonCost: data.addonCost,
    };
  }

  /**
   * Get time until tournament starts
   */
  getTimeUntilStart(tournament: Tournament): string {
    const now = new Date();
    const start = new Date(tournament.startTime);
    const diff = start.getTime() - now.getTime();

    if (diff <= 0) {
      return 'Started';
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  /**
   * Get prize pool breakdown
   */
  getPrizePoolBreakdown(tournament: Tournament): {
    totalBuyIns: number;
    houseRake: number;
    prizePool: number;
    payouts: { position: number; amount: number; percentage: number }[];
  } {
    const totalBuyIns = tournament.currentPlayers * tournament.buyInAmount;
    const { prizePool, houseRake } = calculatePrizePool(totalBuyIns, tournament.houseRakePercent || 10);

    const structure = tournament.payoutStructure.length > 0 
      ? tournament.payoutStructure 
      : getPayoutStructure(tournament.currentPlayers);

    const payouts = structure.map(level => ({
      position: level.position,
      amount: Math.floor(prizePool * (level.percentage / 100) * 100) / 100,
      percentage: level.percentage,
    }));

    return { totalBuyIns, houseRake, prizePool, payouts };
  }

  /**
   * Get ordinal suffix (1st, 2nd, 3rd, etc.)
   */
  private getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

const tournamentService = new TournamentService();
export default tournamentService;